import { useCallback, useEffect, useRef } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'foundations-theming/utils';
import { Button } from 'foundations-components/transitional/components/Button';
import { useOpenAwardModal } from 'academy-common-lib/features/certificate/components/AwardModalProvider';
import { DEFAULT_CONFETTI_WINDOW_MS, startConfetti } from 'academy-common-lib/utils/confetti';
import { jsx as _jsx } from "react/jsx-runtime";
const LikeLink = styled.span.withConfig({
  displayName: "TrackViewAwardLink__LikeLink"
})(["color:", ";cursor:pointer;font-weight:500;transition:all 0.15s ease-out;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;&:hover{text-decoration:underline;color:", ";}"], ({
  theme,
  color
}) => color || theme.color['text-interactive-default'], ({
  theme,
  hoverColor
}) => hoverColor || theme.color['text-interactive-default-hover']);
const AwardLink = styled(LikeLink).withConfig({
  displayName: "TrackViewAwardLink__AwardLink"
})(["color:", ";text-decoration:underline;:hover{color:", ";}"], ({
  theme,
  use
}) => use === 'on-light' ? theme.color['text-interactive-default'] : theme.color['text-interactive-on-fill'], ({
  theme,
  use
}) => use === 'on-light' ? theme.color['text-interactive-default-hover'] : theme.color['text-interactive-on-fill-hover']);
const TrackViewAwardLink = ({
  award,
  certification,
  trackId,
  useCurrentText,
  useButton = true,
  use = useButton ? 'primary' : 'dark',
  expired,
  likeLink,
  confettiAwardedAtMs,
  confettiWindowMs = DEFAULT_CONFETTI_WINDOW_MS
}) => {
  const openAwardModal = useOpenAwardModal();
  const confettiCleanupRef = useRef(undefined);
  const maybeTriggerConfetti = useCallback(() => {
    if (!confettiAwardedAtMs) {
      return;
    }
    if (Date.now() - confettiAwardedAtMs < confettiWindowMs) {
      var _confettiCleanupRef$c;
      (_confettiCleanupRef$c = confettiCleanupRef.current) === null || _confettiCleanupRef$c === void 0 || _confettiCleanupRef$c.call(confettiCleanupRef);
      confettiCleanupRef.current = startConfetti();
    }
  }, [confettiAwardedAtMs, confettiWindowMs]);
  useEffect(() => {
    return () => {
      var _confettiCleanupRef$c2;
      (_confettiCleanupRef$c2 = confettiCleanupRef.current) === null || _confettiCleanupRef$c2 === void 0 || _confettiCleanupRef$c2.call(confettiCleanupRef);
      confettiCleanupRef.current = undefined;
    };
  }, []);
  if (!award || !trackId) {
    return null;
  }
  const handleClick = e => {
    e.stopPropagation();
    e.preventDefault();
    maybeTriggerConfetti();
    openAwardModal(trackId, 'click');
  };
  let label = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "academyCommon.common.showCertificateOfCompletion"
  });
  if (certification) {
    label = useCurrentText ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.common.showCurrentCertification"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.common.showCertification"
    });
  }
  if (expired) {
    label = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyCommon.common.showExpiredCertification"
    });
  }
  if (likeLink) {
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(AwardLink, {
        onClick: handleClick,
        use: use,
        children: label
      })
    );
  }
  if (useButton) {
    return /*#__PURE__*/_jsx(Button, {
      "data-test-id": "view-award-button",
      onClick: handleClick,
      use: use,
      children: label
    });
  }
  return /*#__PURE__*/_jsx(LikeLink, {
    "data-test-id": "view-award-link",
    onClick: handleClick,
    children: label
  });
};
export default TrackViewAwardLink;